import { useMemo, useState } from 'react'
import { MISCONCEPTIONS, type AnswerValue, type Attempt, type Confidence, type MisconceptionCode, type Question } from '../types'
import { isCorrect } from '../lib/assessment'
import { MathText } from './MathText'
import { QuestionAssets } from './QuestionAssets'
import { getSmartQuestionId } from '../lib/smartQuestionId'

type QuestionCardProps = {
  q: Question
  session?: Attempt['session']
  onDone: (attempt: Attempt) => void
  onReflect?: (attemptId: string, misconception: MisconceptionCode) => void
  onContinue?: (attempt: Attempt) => void
  /** Persist a review flag at the surrounding test/session level. */
  flagged?: boolean
  onToggleFlag?: (questionId: string, flagged: boolean) => void
  /** Used only for server-generated instances; local-demo remains synchronous. */
  onGrade?: (input: { questionId: string; answer: AnswerValue; elapsedSeconds: number; confidence: Confidence }) => Promise<{ attemptId: string; correct: boolean; solution: string; hint1: string; hint2: string }>
}

const newAttemptId = () => typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `attempt-${Date.now()}-${Math.random().toString(36).slice(2)}`

export function QuestionCard({ q, session = 'practice', onDone, onReflect, onContinue, onGrade, flagged, onToggleFlag }: QuestionCardProps) {
  const [value, setValue] = useState<AnswerValue>('')
  const [answeredTruths, setAnsweredTruths] = useState<Set<number>>(new Set())
  const [status, setStatus] = useState<'idle' | 'done'>('idle')
  const [hint, setHint] = useState(0)
  const [showSolution, setShowSolution] = useState(true)
  const [confidence, setConfidence] = useState<Confidence>('medium')
  const [misconception, setMisconception] = useState<MisconceptionCode | null>(null)
  const [attempt, setAttempt] = useState<Attempt | null>(null)
  const [graded, setGraded] = useState<{ correct: boolean; solution: string } | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // CBT Tools States
  const [fontScale, setFontScale] = useState<number>(1)
  const [isFlagged, setIsFlagged] = useState<boolean>(false)
  const [eliminated, setEliminated] = useState<Set<string>>(new Set())
  const [showScratchpad, setShowScratchpad] = useState<boolean>(false)
  const [noteText, setNoteText] = useState<string>('')
  
  const started = useMemo(() => Date.now(), [q.id])
  const correct = status === 'done' ? (graded?.correct ?? isCorrect(q, value)) : false
  const isQuestionFlagged = flagged ?? isFlagged

  const hasAnswer = q.type === 'true-false'
    ? answeredTruths.size === (q.choices?.length || 0)
    : Array.isArray(value) ? value.length > 0 : Boolean(value)

  const submit = async (skip: boolean = false) => {
    if (!skip && !hasAnswer) return
    setSubmitError(null)
    setIsSubmitting(true)
    const elapsedSeconds = Math.max(1, Math.round((Date.now() - started) / 1000))
    let result: { attemptId?: string; correct: boolean; solution?: string }
    try {
      if (skip) {
        result = { correct: false }
      } else {
        result = onGrade
          ? await onGrade({ questionId: q.id, answer: value, elapsedSeconds, confidence })
          : { correct: isCorrect(q, value) }
      }
    } catch {
      setSubmitError('Chưa thể chấm câu này. Kiểm tra kết nối rồi thử lại.')
      setIsSubmitting(false)
      return
    }
    const answeredAt = new Date().toISOString()
    const nextAttempt: Attempt = {
      id: result.attemptId || newAttemptId(),
      questionId: q.id,
      answer: value,
      correct: result.correct,
      timeSeconds: elapsedSeconds,
      competency: q.competency,
      knowledge: q.knowledge,
      topic: q.topic,
      variantGroup: q.variantGroup,
      prerequisites: q.prerequisites,
      confidence,
      hintLevel: hint,
      session,
      at: answeredAt,
      serverPersisted: Boolean(result.attemptId),
    }
    setGraded(result.solution ? { correct: result.correct, solution: result.solution } : { correct: result.correct, solution: q.solution })
    setAttempt(nextAttempt)
    setStatus('done')
    onDone(nextAttempt)
    setIsSubmitting(false)
  }

  const chooseMisconception = (code: MisconceptionCode) => {
    if (!attempt) return
    setMisconception(code)
    onReflect?.(attempt.id, code)
  }

  const toggleEliminate = (choiceId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setEliminated(prev => {
      const next = new Set(prev)
      if (next.has(choiceId)) next.delete(choiceId)
      else next.add(choiceId)
      return next
    })
  }

  // Calculate dynamic grid layout for choices based on length
  const choiceGridClass = useMemo(() => {
    if (!q.choices || q.choices.length !== 4) return 'options-list'
    const maxLen = Math.max(...q.choices.map(c => c.text.length))
    if (maxLen <= 8) return 'options-list grid-4x1'
    if (maxLen <= 25) return 'options-list grid-2x2'
    return 'options-list'
  }, [q.choices])

  return (
    <article className="question-content" style={{ fontSize: `${fontScale * 1.15}rem` }}>
      {/* Header Meta & CBT Study Toolbar */}
      <div className="question-content-header-meta flex justify-between items-center mb-4 pb-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div className="flex gap-2 items-center flex-wrap">
          <span className="tag-pill orange">{q.exam}</span>
          <span className="tag-pill question-trace-code" title={`Mã truy vết: ${getSmartQuestionId(q)}`}>
            {getSmartQuestionId(q)}
          </span>
          <span className="tag-pill">{q.topic}</span>
          {q.passageText && (
            <span className="tag-pill green">
              📑 Câu hỏi chùm
            </span>
          )}
          <span className="tag-pill" style={{ background: '#f1f5f9', color: 'var(--text-muted)' }}>
            Mức {q.difficulty}/5 • {q.cognitiveLevel}
          </span>
        </div>

        {/* CBT Interactive Study Tools */}
        <div className="cbt-study-toolbar">
          <div className="tool-group" title="Tăng/Giảm cỡ chữ">
            <button 
              className="tool-btn" 
              onClick={() => setFontScale(s => Math.max(0.85, Number((s - 0.1).toFixed(2))))}
              disabled={fontScale <= 0.85}
              title="Thu nhỏ chữ"
            >
              A-
            </button>
            <span className="tool-scale-text">{Math.round(fontScale * 100)}%</span>
            <button 
              className="tool-btn" 
              onClick={() => setFontScale(s => Math.min(1.35, Number((s + 0.1).toFixed(2))))}
              disabled={fontScale >= 1.35}
              title="Phóng to chữ"
            >
              A+
            </button>
          </div>

          <button 
            className={`tool-btn-pill ${showScratchpad ? 'active' : ''}`}
            onClick={() => setShowScratchpad(!showScratchpad)}
            title="Bảng nháp & Ghi chú suy luận cá nhân"
          >
            📝 Nháp nhanh {noteText.trim() ? '•' : ''}
          </button>

          <button 
            className={`tool-btn-pill ${isQuestionFlagged ? 'flagged' : ''}`}
            onClick={() => onToggleFlag ? onToggleFlag(q.id, !isQuestionFlagged) : setIsFlagged(!isQuestionFlagged)}
            title="Đánh dấu câu hỏi cần xem lại sau"
          >
            {isQuestionFlagged ? '🚩 Đã đánh dấu' : '🏳️ Đánh dấu'}
          </button>
        </div>
      </div>

      {/* Collapsible Scratchpad Drawer */}
      {showScratchpad && (
        <div className="scratchpad-box mb-4">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-xs font-bold flex items-center gap-1" style={{ color: '#8c6b00' }}>
              📝 BẢNG NHÁP & GHI CHÚ CÁ NHÂN (TỰ ĐỘNG LƯU TẠM)
            </span>
            {noteText && (
              <button 
                onClick={() => setNoteText('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: '#8c6b00', textDecoration: 'underline' }}
              >
                Xóa ghi chú
              </button>
            )}
          </div>
          <textarea 
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="Viết ghi chú, công thức hoặc suy luận nháp tại đây..."
            rows={3}
            className="scratchpad-textarea"
          />
        </div>
      )}

      {/* Render passage/group context if available */}
      {q.passageText && (
        <div 
          className="passage-box mb-6" 
          style={{ 
            position: 'sticky', 
            top: '6px', 
            zIndex: 10,
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <div className="passage-header">
            📑 {q.passageTitle || 'Ngữ cảnh / Dữ liệu dùng chung'}
          </div>
          <div style={{ color: 'var(--text-primary)', lineHeight: 1.7 }}>
            <MathText text={q.passageText} />
          </div>
          <QuestionAssets assets={q.assets} placement="passage" />
        </div>
      )}

      {!q.passageText && <QuestionAssets assets={q.assets} placement="passage" />}
      
      <h3 className="mb-6" style={{ fontSize: `${fontScale * 1.35}rem`, fontWeight: 700, color: 'var(--text-primary)' }}>
        <MathText text={q.stem} />
      </h3>
      <QuestionAssets assets={q.assets} placement="stem" />
      
      {q.type === 'numeric' ? (
        <input 
          style={{ 
            width: '100%', 
            padding: '1.1rem 1.5rem', 
            fontSize: '1.25rem', 
            borderRadius: '12px', 
            border: '2px solid var(--border-color)', 
            background: '#ffffff', 
            color: 'var(--text-primary)',
            outline: 'none'
          }}
          value={typeof value === 'string' ? value : ''}
          onChange={e => setValue(e.target.value)} 
          placeholder="Nhập kết quả số của bạn..." 
          disabled={status === 'done'} 
        />
      ) : q.type === 'true-false' ? (
        <div className="true-false-list">
          {q.choices?.map((choice, index) => {
            const answerValues = Array.isArray(value) ? value as boolean[] : []
            const selected = answeredTruths.has(index) ? answerValues[index] : undefined
            const correctValues = Array.isArray(q.answer) ? q.answer as boolean[] : []
            const isDone = status === 'done'
            const choose = (nextValue: boolean) => {
              setValue(previous => {
                const values = Array.isArray(previous) ? [...previous] as boolean[] : Array(q.choices?.length || 0).fill(false)
                values[index] = nextValue
                return values
              })
              setAnsweredTruths(previous => new Set(previous).add(index))
            }
            return (
              <div className="true-false-row" key={choice.id}>
                <span><b>{String.fromCharCode(97 + index)}.</b> <MathText text={choice.text} /></span>
                <div className="true-false-actions" role="group" aria-label={`Đúng hoặc sai cho mệnh đề ${index + 1}`}>
                  {[true, false].map(option => {
                    const chosen = selected === option
                    const wrong = isDone && chosen && correctValues[index] !== option
                    const right = isDone && correctValues[index] === option
                    return <button
                      key={String(option)} type="button" disabled={isDone}
                      className={`truth-option ${chosen ? 'selected' : ''} ${right ? 'correct-ans' : ''} ${wrong ? 'wrong-ans' : ''}`}
                      onClick={() => choose(option)}
                    >{option ? 'Đúng' : 'Sai'}</button>
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className={choiceGridClass}>
          {q.choices?.map(x => {
            const selectedValues = q.type === 'multi-select' && Array.isArray(value) ? value as string[] : []
            const isSelected = q.type === 'multi-select' ? selectedValues.includes(x.id) : value === x.id
            const isDone = status === 'done'
            const correctValues = Array.isArray(q.answer) ? q.answer as string[] : []
            const isCorrectOption = isDone && (q.type === 'multi-select' ? correctValues.includes(x.id) : x.id === q.answer)
            const isWrongSelected = isDone && isSelected && !isCorrectOption
            const isEliminated = eliminated.has(x.id)
            
            let btnClass = `option-btn opt-${x.id}`
            if (isSelected && !isDone) btnClass += ' selected'
            if (isCorrectOption) btnClass += ' correct-ans'
            if (isWrongSelected) btnClass += ' wrong-ans'
            if (isEliminated) btnClass += ' eliminated'

            return (
              <button 
                key={x.id}
                disabled={status === 'done'} 
                onClick={() => {
                  if (q.type === 'multi-select') {
                    setValue(previous => {
                      const values = Array.isArray(previous) ? previous as string[] : []
                      return values.includes(x.id) ? values.filter(id => id !== x.id) : [...values, x.id]
                    })
                    return
                  }
                  if (isEliminated) {
                    setEliminated(prev => {
                      const next = new Set(prev)
                      next.delete(x.id)
                      return next
                    })
                    setValue(x.id)
                  } else {
                    setValue(x.id)
                  }
                }}
                onDoubleClick={(e) => {
                  e.preventDefault()
                  if (status === 'done') return
                  setEliminated(prev => {
                    const next = new Set(prev)
                    if (next.has(x.id)) {
                      next.delete(x.id)
                    } else {
                      next.add(x.id)
                      if (value === x.id) setValue('')
                    }
                    return next
                  })
                }}
                onContextMenu={(e) => {
                  e.preventDefault()
                  if (status === 'done') return
                  setEliminated(prev => {
                    const next = new Set(prev)
                    if (next.has(x.id)) {
                      next.delete(x.id)
                    } else {
                      next.add(x.id)
                      if (value === x.id) setValue('')
                    }
                    return next
                  })
                }}
                className={btnClass}
                title={isEliminated ? "Nhấp đúp hoặc bấm để khôi phục đáp án" : "1-Click: Chọn đáp án • Double-Click/Chuột phải: Gạch loại trừ"}
                style={{ width: '100%' }}
              >
                <span className="option-letter">{x.id}</span>
                <span style={{ textDecoration: isEliminated ? 'line-through' : 'none', opacity: isEliminated ? 0.45 : 1 }}>
                  <MathText text={x.text} />
                </span>
                {isEliminated && (
                  <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: 'var(--error)', fontWeight: 800 }}>
                    [Đã loại]
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {status === 'idle' && (
        <div className="flex justify-between items-center mt-8 pt-4" style={{ borderTop: '1px solid var(--border-color)' }}>
          <button className="btn btn-secondary" onClick={() => setHint(Math.min(3, hint + 1))}>
            💡 Gợi ý tư duy {hint > 0 ? `(Nấc ${hint}/3)` : ''}
          </button>
          <div className="flex flex-col items-end gap-2">
            <span className="text-muted" style={{ fontSize: '0.78rem', fontWeight: 700 }}>Bạn chắc đáp án này đến đâu?</span>
            <div className="flex gap-1.5" role="group" aria-label="Mức độ tự tin">
              {([
                ['high', 'Chắc chắn'],
                ['medium', 'Phân vân'],
                ['low', 'Đoán'],
              ] as [Confidence, string][]).map(([level, label]) => (
                <button
                  key={level}
                  type="button"
                  className={`confidence-btn ${confidence === level ? 'selected' : ''}`}
                  onClick={() => setConfidence(level)}
                  aria-pressed={confidence === level}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-1">
              <button className="btn btn-ghost" onClick={() => void submit(true)} disabled={isSubmitting}>
                Bỏ qua & Xem lời giải
              </button>
              <button className="btn btn-primary" onClick={() => void submit()} disabled={!hasAnswer || isSubmitting}>
                {isSubmitting ? 'Đang chấm…' : 'Trả lời →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {submitError && <p role="alert" className="text-sm" style={{ color: 'var(--error)', marginTop: '0.6rem' }}>{submitError}</p>}

      {/* Nút xem lời giải — chỉ hiện sau khi đã nộp bài */}
      {status === 'done' && (
        <div className="mt-4">
          <button
            className="btn btn-secondary"
            onClick={() => setShowSolution(s => !s)}
            style={{ width: '100%', justifyContent: 'center', fontWeight: 700 }}
          >
            {showSolution ? '▲ Ẩn lời giải' : '📖 Xem lời giải chi tiết'}
          </button>
        </div>
      )}

      {hint > 0 && status === 'idle' && (
        <div className="explanation-box" style={{ background: 'var(--orange-bg)', borderColor: 'var(--orange-primary)', animation: 'popIn 0.25s ease-out' }}>
          <div className="flex justify-between items-center mb-2">
            <b className="text-accent" style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              💡 Nấc {hint}/3: {hint === 1 ? '🎯 Định hướng chìa khóa' : hint === 2 ? '📊 Sơ đồ/Bảng suy luận trung gian' : '⚡ Chốt bước loại trừ cuối'}
            </b>
            {hint < 3 && (
              <button 
                onClick={() => setHint(hint + 1)}
                style={{ fontSize: '0.8rem', background: 'var(--orange-primary)', color: 'white', padding: '0.2rem 0.6rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 700 }}
              >
                Mở Nấc {hint + 1} &rarr;
              </button>
            )}
          </div>
          
          <div style={{ lineHeight: 1.7, fontSize: '0.98rem' }}>
            {hint >= 1 && (
              <div className="mb-2">
                <span style={{ fontWeight: 700, color: 'var(--orange-primary)' }}>• Nấc 1: </span>
                <MathText text={q.hint1 || `Xác định đại lượng cần tính trong bài thuộc chủ đề "${q.topic}", rồi viết dữ kiện đề cho thành biểu thức toán trước khi tính.`} />
              </div>
            )}
            {hint >= 2 && (
              <div className="mb-2 pt-2" style={{ borderTop: '1px dashed #cbd5e1' }}>
                <span style={{ fontWeight: 700, color: '#0284c7' }}>• Nấc 2 (Trung gian): </span>
                <MathText text={q.hint2 || 'Thay số liệu vào biểu thức/công thức ở nấc 1, tính đến kết quả cuối rồi đối chiếu 4 phương án để chọn giá trị gần nhất.'} />
              </div>
            )}
            {hint === 3 && (
              <div className="pt-2" style={{ borderTop: '1px dashed #cbd5e1' }}>
                <span style={{ fontWeight: 700, color: 'var(--green-primary)' }}>• Nấc 3 (Chốt): </span>
                <span>Kiểm tra lại điều kiện, đơn vị và phương án bạn chọn. Hệ thống chỉ hiện đáp án sau khi bạn nộp bài.</span>
              </div>
            )}
          </div>
        </div>
      )}

      {status === 'done' && (
        <div className="explanation-box">
          <h4 className="mb-2" style={{ color: correct ? 'var(--green-primary)' : 'var(--error)', fontSize: '1.2rem' }}>
            {correct ? '✅ Chính xác! Tuyệt vời.' : '❌ Chưa chính xác. Hãy xem lời giải nhé!'}
          </h4>
          {showSolution && (
            <div style={{ color: 'var(--text-primary)' }}>
              <b style={{ color: 'var(--orange-primary)' }}>Lời giải chi tiết: </b>
              <div className="mt-2" style={{ lineHeight: 1.7 }}>
                  <MathText text={graded?.solution || q.solution} />
              </div>
              <QuestionAssets assets={q.assets} placement="solution" />
          </div>
          )}

          {!correct && (
            <div className="reflection-box mt-4" aria-labelledby={`reflection-${q.id}`}>
              <h5 id={`reflection-${q.id}`} style={{ margin: '0 0 0.45rem', fontSize: '1rem' }}>Khoanh đúng nguyên nhân để hệ thống sửa đúng lỗi</h5>
              <p className="text-muted" style={{ margin: '0 0 0.75rem', fontSize: '0.88rem' }}>Không có đáp án phạt: chọn điều gần nhất với cách bạn vừa nghĩ.</p>
              <div className="reflection-options">
                {[...q.misconceptionOptions, 'unknown' as MisconceptionCode]
                  .filter((code, index, all) => all.indexOf(code) === index)
                  .map(code => (
                    <button
                      type="button"
                      key={code}
                      className={`reflection-option ${misconception === code ? 'selected' : ''}`}
                      onClick={() => chooseMisconception(code)}
                      aria-pressed={misconception === code}
                    >
                      {MISCONCEPTIONS[code]}
                    </button>
                  ))}
              </div>
            </div>
          )}

          {onContinue && attempt && (
            <div className="flex justify-end mt-4">
              <button
                className="btn btn-primary"
                onClick={() => onContinue(attempt)}
                disabled={!correct && !misconception}
              >
                {correct ? 'Câu tiếp theo →' : 'Đã ghi nhận lỗi, luyện tiếp →'}
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  )
}
